import { useRef, useState } from "react";
import { Upload, X, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";

export const ImageUploader = ({ images, onChange, multiple = true, testId = "image-uploader" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const upload = async (files) => {
    const list = Array.from(files).slice(0, multiple ? 10 : 1);
    if (!list.length) return;
    setUploading(list.length);
    const urls = [];
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload failed: ${file.name}`);
      }
      setUploading((n) => n - 1);
    }
    if (urls.length) onChange(multiple ? [...images, ...urls] : urls);
  };

  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-3 gap-2">
        {images.map((u, i) => (
          <div key={u + i} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-border" data-testid={`${testId}-item-${i}`}>
            <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
            {i === 0 && multiple && <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground rounded px-1.5 py-0.5 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>}
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} data-testid={`${testId}-remove-${i}`}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
            {i > 0 && multiple && (
              <button type="button" onClick={() => onChange([u, ...images.filter((_, j) => j !== i)])}
                className="absolute bottom-1 right-1 text-[10px] bg-black/60 text-white rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Set cover</button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading > 0} data-testid={`${testId}-add-button`}
          className="aspect-[4/3] rounded-lg border-2 border-dashed border-border hover:border-primary/60 hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground">
          {uploading > 0 ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Upload className="h-5 w-5 text-primary" />}
          {uploading > 0 ? `Uploading ${uploading}...` : multiple ? "Add photos" : "Upload photo"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple={multiple} className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`${testId}-file-input`} />
      <p className="mt-1.5 text-[11px] text-muted-foreground">JPG, PNG or WEBP up to 8 MB. Take a photo on your phone or pick from your laptop.</p>
    </div>
  );
};
